import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ServiceAppointment,
  ServiceAppointmentCreate,
  ServiceAppointmentListQuery,
  ServiceAppointmentListResponse,
  ServiceAppointmentStatusCounts
} from '../models/service-appointment.model';

/**
 * Service Appointment Service
 * Handles service appointment CRUD operations
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceAppointmentService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get appointments for an organization with optional filtering
   */
  async getOrganizationAppointments(
    organizationId: string,
    query?: ServiceAppointmentListQuery
  ): Promise<ServiceAppointmentListResponse> {
    let params = new HttpParams();

    if (query?.status) {
      params = params.set('status', query.status);
    }
    if (query?.page) {
      params = params.set('page', query.page.toString());
    }
    if (query?.limit) {
      params = params.set('limit', query.limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<ServiceAppointmentListResponse>(
        `${this.apiUrl}/service-appointments/organization/${organizationId}`,
        { params }
      )
    );
    return response;
  }

  /**
   * Get appointment status counts for an organization
   */
  async getOrganizationAppointmentCounts(organizationId: string): Promise<ServiceAppointmentStatusCounts> {
    const response = await firstValueFrom(
      this.http.get<{ counts: ServiceAppointmentStatusCounts }>(
        `${this.apiUrl}/service-appointments/organization/${organizationId}/counts`
      )
    );
    return response.counts;
  }

  /**
   * Get a specific appointment by ID
   */
  async getAppointment(id: string): Promise<ServiceAppointment | null> {
    const response = await firstValueFrom(
      this.http.get<{ appointment: ServiceAppointment }>(
        `${this.apiUrl}/service-appointments/${id}`
      )
    );
    return response.appointment || null;
  }

  /**
   * Create a new service appointment
   */
  async createAppointment(data: ServiceAppointmentCreate): Promise<ServiceAppointment> {
    const response = await firstValueFrom(
      this.http.post<{ appointment: ServiceAppointment }>(
        `${this.apiUrl}/service-appointments`,
        data
      )
    );
    return response.appointment;
  }

  /**
   * Cancel a service appointment
   */
  async cancelAppointment(id: string, reason: string): Promise<void> {
    await firstValueFrom(
      this.http.patch<void>(
        `${this.apiUrl}/service-appointments/${id}/cancel`,
        { reason }
      )
    );
  }
}
